import { MEETINGS_BACK_URL } from "@/config";
import { useAppSelector } from "@/hooks/redux-hooks";
import { createContext, PropsWithChildren, useContext, useEffect } from "react";
import { io } from "socket.io-client";

const socket = io(MEETINGS_BACK_URL, { transports: ["websocket"] });

const SocketContext = createContext(socket);

const SocketProvider = ({ children }: PropsWithChildren) => {
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    if (!user?.personalRoomId || !user?.companyUniqueName) return;

    const joinPayload = {
      personalRoomId: user.personalRoomId,
      companyName: user.companyUniqueName,
    };

    // Initial emit. socket.io buffers and flushes on connect if the
    // underlying transport isn't open yet.
    socket.emit("agent:join", joinPayload);

    // Re-emit on every (re)connect so the agent stays subscribed across
    // network drops and server restarts. Without this, a reconnect leaves
    // the agent silent — no waiting-room:join / session.* events flow
    // until the page is refreshed.
    const onConnect = () => {
      socket.emit("agent:join", joinPayload);
    };
    socket.on("connect", onConnect);

    return () => {
      socket.off("connect", onConnect);
    };
  }, [user?.personalRoomId, user?.companyUniqueName]);

  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};

export default SocketProvider;

export const useSocketContext = () => useContext(SocketContext);
